import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, AlertCircle, CheckCircle2, ShieldAlert, Loader2 } from 'lucide-react';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { clearForcePasswordChange } from '@/state/authSlice';
import { authApi } from '@/api/auth.api';
import { AppRoutes } from '@/constants/routes';

// ── Schema ────────────────────────────────────────────────────────────────────
const schema = z
  .object({
    currentPassword: z.string().min(1, 'Current password is required'),
    // The character-class rules are the company's password policy, enforced authoritatively by the
    // server — the client no longer hard-blocks them, or it would demand (e.g.) a special character
    // even when the policy has that turned off, contradicting the configured policy (QA #79). The
    // checklist below stays as guidance; the server returns the precise rule if one is violated.
    newPassword: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  })
  .refine((d) => d.currentPassword !== d.newPassword, {
    message: 'New password must be different from current password',
    path: ['newPassword'],
  });

type FormValues = z.infer<typeof schema>;

// ── Password requirement item ─────────────────────────────────────────────────
const Requirement = ({ met, text }: { met: boolean; text: string }) => (
  <div className={`flex items-center gap-1.5 text-[11px] ${met ? 'text-green-600 dark:text-green-400' : 'text-slate-400 dark:text-gray-500'}`}>
    <CheckCircle2 size={11} className={met ? 'text-green-500' : 'text-slate-300'} />
    {text}
  </div>
);

// ── Component ─────────────────────────────────────────────────────────────────
export const ChangePasswordPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const authUser = useAppSelector((s) => s.auth.user);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [apiError,    setApiError]    = useState<string | null>(null);
  const [success,     setSuccess]     = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const newPwd = watch('newPassword') ?? '';

  // Live requirement checks
  const reqs = {
    length:  newPwd.length >= 8,
    upper:   /[A-Z]/.test(newPwd),
    lower:   /[a-z]/.test(newPwd),
    number:  /[0-9]/.test(newPwd),
    special: /[^A-Za-z0-9]/.test(newPwd),
  };

  const onSubmit = async (values: FormValues) => {
    setApiError(null);

    try {
      await authApi.changePassword({
        currentPassword: values.currentPassword,
        newPassword:     values.newPassword,
      });

      // Clear the force-change flag in Redux so the user can proceed normally
      dispatch(clearForcePasswordChange());
      setSuccess(true);

      // Navigate to dashboard after 1.5 s
      setTimeout(() => navigate(AppRoutes.DASHBOARD, { replace: true }), 1500);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string; errors?: unknown } } };
      const detail   = axiosErr?.response?.data;
      // errors is [{ field, message }] — read the message, not the object (else "[object Object]").
      const arr = Array.isArray(detail?.errors) ? detail!.errors as Array<{ message?: string }> : [];
      const firstMsg = arr.map(e => (typeof e === 'string' ? e : e?.message)).filter(Boolean)[0];
      setApiError(firstMsg ?? detail?.message ?? 'Failed to change password. Please try again.');
    }
  };

  const inputCls = (hasErr: boolean) =>
    `w-full pl-9 pr-10 py-2.5 rounded-lg text-sm border outline-none transition-all focus:ring-2 ${
      hasErr
        ? 'border-red-300 bg-red-50 dark:bg-red-900/20 focus:ring-red-200'
        : 'border-slate-200 dark:border-gray-700 bg-white dark:bg-slate-800 focus:ring-blue-100 focus:border-blue-400'
    }`;

  const isForced = authUser?.forcePasswordChange;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">

        {/* Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-slate-100 dark:border-gray-800 overflow-hidden">

          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-500 px-6 py-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
                <Lock size={18} className="text-white" />
              </div>
              <div>
                <h1 className="text-white font-bold text-lg leading-tight">Change Password</h1>
                <p className="text-blue-100 text-xs mt-0.5">Update your account password</p>
              </div>
            </div>
          </div>

          {/* Force-change notice */}
          {isForced && (
            <div className="flex items-start gap-2.5 px-6 py-3 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-100">
              <ShieldAlert size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-amber-700 dark:text-amber-400 text-xs leading-relaxed">
                Your password must be changed before you can continue. This may be due to an
                administrator reset or your password reaching its expiry date.
              </p>
            </div>
          )}

          <div className="px-6 py-6">

            {/* Success state */}
            {success ? (
              <div className="flex flex-col items-center gap-3 py-4 text-center">
                <div className="w-14 h-14 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center">
                  <CheckCircle2 size={28} className="text-green-500" />
                </div>
                <div>
                  <p className="font-semibold text-slate-800 dark:text-gray-200">Password changed successfully!</p>
                  <p className="text-slate-500 dark:text-gray-400 text-sm mt-1">Redirecting you to the dashboard…</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">

                {/* API error */}
                {apiError && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 rounded-lg text-red-700 dark:text-red-400 text-sm">
                    <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                    <span>{apiError}</span>
                  </div>
                )}

                {/* Current password */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-gray-400 mb-1.5">
                    Current Password
                  </label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      {...register('currentPassword')}
                      type={showCurrent ? 'text' : 'password'}
                      placeholder="Enter current password"
                      autoComplete="current-password"
                      className={inputCls(!!errors.currentPassword)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrent((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showCurrent ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  {errors.currentPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.currentPassword.message}</p>
                  )}
                </div>

                {/* New password */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-gray-400 mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      {...register('newPassword')}
                      type={showNew ? 'text' : 'password'}
                      placeholder="Enter new password"
                      autoComplete="new-password"
                      className={inputCls(!!errors.newPassword)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNew((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showNew ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  {errors.newPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.newPassword.message}</p>
                  )}

                  {/* Live requirements */}
                  {newPwd.length > 0 && (
                    <div className="mt-2 grid grid-cols-2 gap-1 p-2.5 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-100 dark:border-gray-800">
                      <Requirement met={reqs.length}  text="At least 8 characters" />
                      <Requirement met={reqs.upper}   text="Uppercase letter" />
                      <Requirement met={reqs.lower}   text="Lowercase letter" />
                      <Requirement met={reqs.number}  text="Number" />
                      <Requirement met={reqs.special} text="Special character" />
                    </div>
                  )}
                </div>

                {/* Confirm password */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 dark:text-gray-400 mb-1.5">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                    <input
                      {...register('confirmPassword')}
                      type={showConfirm ? 'text' : 'password'}
                      placeholder="Confirm new password"
                      autoComplete="new-password"
                      className={inputCls(!!errors.confirmPassword)}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirm((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      tabIndex={-1}
                    >
                      {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  {errors.confirmPassword && (
                    <p className="text-red-500 text-xs mt-1">{errors.confirmPassword.message}</p>
                  )}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-3 pt-2">
                  {!isForced && (
                    <button
                      type="button"
                      onClick={() => navigate(-1)}
                      className="flex-1 py-2.5 rounded-lg border border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                    >
                      Cancel
                    </button>
                  )}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <><Loader2 size={14} className="animate-spin" /> Changing…</>
                    ) : (
                      'Change Password'
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 dark:text-gray-500 mt-4">
          myLedger ONE · General Ledger
        </p>
      </div>
    </div>
  );
};
